import React, { useState, useEffect, useRef, Component, type ErrorInfo } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ClerkProvider, SignIn, SignUp, Show, useClerk, useUser } from '@clerk/react';
import { publishableKeyFromHost } from '@clerk/react/internal';
import { shadcn } from '@clerk/themes';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import NotFound from '@/pages/not-found';
import { Route, Switch, Router as WouterRouter, useLocation, Redirect } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, Home, BookOpen, FileText, ClipboardList,
  Monitor, FolderOpen, Settings, LogOut, ChevronRight,
  Sparkles, Menu, Trash2, BookMarked, Copy, Library,
  AlertTriangle, AlertCircle, RefreshCw, X, Shield,
} from 'lucide-react';
import { ResourcesProvider, useResources, type SavedResource } from './resources-context';
import { ProfileProvider, useProfile } from './ProfileContext';
import { BillingProvider, useBilling } from './BillingContext';
import { TrialBanner } from './components/TrialBanner';
import { WelcomeBackBanner } from './components/WelcomeBackBanner';
import { LockedState } from './components/LockedState';
import { UpgradePage } from './pages/UpgradePage';
import { AccountStatusPage } from './pages/AccountStatusPage';
import { LessonPlanner } from './LessonPlanner';
import { LessonWorkspace } from './LessonWorkspace';
import { ActivitySheet } from './ActivitySheet';
import { CurriculumHub } from './CurriculumHub';
import { AssessmentGenerator } from './AssessmentGenerator';
import { PresentationGenerator } from './PresentationGenerator';
import { SettingsPage } from './SettingsPage';
import { OnboardingWizard } from './OnboardingWizard';
import { MyResourcesPage } from './MyResourcesPage';
import { HelpFeedbackButton } from './HelpFeedbackButton';
import { WelcomeTour } from './WelcomeTour';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsPage } from './pages/TermsPage';
import { HomePage } from './pages/HomePage';
import { AdminMessagesPage } from './pages/AdminMessagesPage';
import { setSentryUser } from './sentry';

// ── Clerk setup — copy verbatim (see clerk-auth skill) ───────────────────────
const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);
// Empty in dev (intentional — Clerk hits dev FAPI directly). Auto-populated in prod.
// Do NOT gate on NODE_ENV/import.meta.env.PROD — any branching breaks the prod proxy.
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');

// Clerk passes full paths to routerPush/routerReplace; wouter prepends base — strip to avoid doubling.
function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || '/'
    : path;
}

function getGreeting(name: string): string {
  const h = new Date().getHours();
  const part = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
  return `Good ${part}, ${name}!`;
}

if (!clerkPubKey) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY');
}

const queryClient = new QueryClient();

// ── Clerk appearance: TeachBud blue ──────────────────────────────────────────
const clerkAppearance = {
  theme: shadcn,
  cssLayerName: 'clerk',
  options: {
    logoPlacement: 'inside' as const,
    logoLinkUrl: basePath || '/',
    logoImageUrl: `${window.location.origin}${basePath}/logo.png`,
    socialButtonsPlacement: 'bottom' as const,
  },
  variables: {
    colorPrimary: '#2563eb',
    colorForeground: '#0f172a',
    colorMutedForeground: '#64748b',
    colorDanger: '#ef4444',
    colorBackground: '#ffffff',
    colorInput: '#ffffff',
    colorInputForeground: '#0f172a',
    colorNeutral: '#e2e8f0',
    fontFamily: 'inherit',
    borderRadius: '12px',
  },
  elements: {
    rootBox: 'w-full flex justify-center',
    cardBox: 'bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 w-[440px] max-w-full overflow-hidden',
    card: '!shadow-none !border-0 !bg-transparent !rounded-none',
    footer: '!shadow-none !border-0 !bg-transparent !rounded-none',
    headerTitle: 'text-slate-900 font-extrabold',
    headerSubtitle: 'text-slate-500',
    socialButtonsBlockButtonText: 'text-slate-700 font-medium',
    formFieldLabel: 'text-slate-700 font-semibold text-sm',
    footerActionLink: 'text-primary font-semibold',
    footerActionText: 'text-slate-500',
    dividerText: 'text-slate-400',
    identityPreviewEditButton: 'text-primary',
    formFieldSuccessText: 'text-emerald-600',
    alertText: 'text-red-600',
    logoBox: 'mb-1',
    logoImage: 'w-10 h-10',
    socialButtonsBlockButton: 'border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors',
    formButtonPrimary: 'bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl shadow-sm',
    formFieldInput: 'border-slate-200 focus:border-primary rounded-xl',
    footerAction: 'bg-transparent',
    dividerLine: 'bg-slate-200',
    alert: 'bg-red-50 border-red-100 rounded-xl',
    otpCodeFieldInput: 'border-slate-200 rounded-xl',
  },
};

// ── Error Boundary ────────────────────────────────────────────────────────────
interface ErrorBoundaryState { error: Error | null }
interface ErrorBoundaryState { error: Error | null; errorRef: string }

function ErrorBoundaryFallback({ errorRef }: { errorRef: string }) {
  const bPath = import.meta.env.BASE_URL?.replace(/\/$/, '') ?? '';
  const [showReport, setShowReport] = useState(false);
  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-8">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center mx-auto">
          <AlertTriangle size={22} className="text-red-500" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-1">Something went wrong</h2>
          <p className="text-slate-500 text-sm">An unexpected error occurred. Your lesson content is safe in your account.</p>
          <p className="text-xs text-slate-400 mt-1 font-mono">Error ref: {errorRef}</p>
        </div>
        <div className="flex flex-col gap-2.5 items-center">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <RefreshCw size={14} />
            Reload TeachBud
          </button>
          <button
            onClick={() => { window.location.href = bPath + '/dashboard'; }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Return to Dashboard
          </button>
          <button
            onClick={() => setShowReport(true)}
            className="text-sm text-slate-500 hover:text-primary transition-colors underline focus-visible:outline-none"
          >
            Report this problem
          </button>
        </div>
      </div>
      {showReport && (
        <HelpFeedbackButton
          currentPage={`error-boundary (ref: ${errorRef})`}
          initialOpen
          initialCategory="bug"
        />
      )}
    </div>
  );
}

class ErrorBoundary extends Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, errorRef: '' };
  static getDerivedStateFromError(error: Error) {
    const ref = Math.random().toString(36).slice(2, 8).toUpperCase();
    return { error, errorRef: ref };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[TeachBud] Unhandled error:', error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return <ErrorBoundaryFallback errorRef={this.state.errorRef} />;
    }
    return this.props.children;
  }
}

// ── ClerkQueryClientCacheInvalidator ──────────────────────────────────────────
function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        qc.clear();
      }
      prevUserIdRef.current = userId;
      // Tag the authenticated user in Sentry (userId only — no name/email)
      setSentryUser(userId);
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

// ── Shared: TeachBud brand header ─────────────────────────────────────────────
function BrandHeader({ tagline = 'Everything a teacher needs. Created in minutes.' }: { tagline?: string }) {
  const bPath = import.meta.env.BASE_URL?.replace(/\/$/, '') ?? '';
  const [, navigate] = useLocation();
  return (
    <div className="flex flex-col items-center mb-8 text-center">
      {/* Gradient border frame — black × green */}
      <div
        className="rounded-2xl p-[3px] mb-4"
        style={{
          background: 'linear-gradient(135deg, #059669 0%, #0f172a 38%, #059669 62%, #0f172a 100%)',
          boxShadow: '0 4px 16px rgba(5,150,105,0.30), 0 2px 6px rgba(0,0,0,0.35)',
        }}
      >
        <button
          onClick={() => navigate('/')}
          className="block bg-white rounded-[13px] p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label="Go to TeachBud homepage"
        >
          <img
            src={`${bPath}/logo.png`}
            alt="TeachBud"
            className="h-40 w-auto mx-auto"
          />
        </button>
      </div>
      <p className="text-slate-500 text-[15px]">{tagline}</p>
    </div>
  );
}

// ── Shared: sidebar brand mark ────────────────────────────────────────────────
function BrandMark({ onHomeClick }: { onHomeClick?: () => void }) {
  const bPath = import.meta.env.BASE_URL?.replace(/\/$/, '') ?? '';
  return (
    <div className="flex items-center">
      {/* Gradient border frame — black × green */}
      <div
        className="rounded-2xl p-[3px]"
        style={{
          background: 'linear-gradient(135deg, #059669 0%, #0f172a 38%, #059669 62%, #0f172a 100%)',
          boxShadow: '0 4px 16px rgba(5,150,105,0.28), 0 2px 6px rgba(0,0,0,0.32)',
        }}
      >
        <button
          onClick={onHomeClick}
          className="block bg-white rounded-[13px] p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label="Go to dashboard home"
          disabled={!onHomeClick}
        >
          <img
            src={`${bPath}/logo.png`}
            alt="TeachBud"
            className="h-16 w-auto"
          />
        </button>
      </div>
    </div>
  );
}

// ── Clerk sign-in page ────────────────────────────────────────────────────────
function SignInPage() {
  return (
    <div className="min-h-[100dvh] w-full flex flex-col items-center justify-center bg-slate-50 px-4 relative overflow-hidden">
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-blue-200/30 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-cyan-200/30 blur-[120px] pointer-events-none" />
      <div className="mb-6 z-10">
        <BrandHeader tagline="Welcome back, teacher." />
      </div>
      <div className="z-10 w-full flex justify-center">
        {/* path MUST be the full browser path — Clerk reads window.location.pathname directly */}
        <SignIn routing="path" path={`${basePath}/sign-in`} signUpUrl={`${basePath}/sign-up`} />
      </div>
    </div>
  );
}

// ── Clerk sign-up page ────────────────────────────────────────────────────────
function SignUpPage() {
  return (
    <div className="min-h-[100dvh] w-full flex flex-col items-center justify-center bg-slate-50 px-4 relative overflow-hidden">
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-blue-200/30 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-cyan-200/30 blur-[120px] pointer-events-none" />
      <div className="mb-6 z-10">
        <BrandHeader tagline="Start creating better lessons today." />
      </div>
      <div className="z-10 w-full flex justify-center">
        <SignUp routing="path" path={`${basePath}/sign-up`} signInUrl={`${basePath}/sign-in`} />
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
type NavItem =
  | 'home' | 'lesson-planner' | 'lesson-workspace'
  | 'activity-sheets' | 'assessments'
  | 'presentations' | 'my-resources' | 'curriculum-hub' | 'settings';

const NAV_ITEMS: { id: NavItem; label: string; icon: React.ElementType }[] = [
  { id: 'home',            label: 'Home',            icon: Home },
  { id: 'lesson-planner',  label: 'Lesson Planner',  icon: BookOpen },
  { id: 'activity-sheets', label: 'Activity Sheets', icon: FileText },
  { id: 'presentations',   label: 'Presentations',   icon: Monitor },
  { id: 'assessments',     label: 'Assessments',     icon: ClipboardList },
  { id: 'my-resources',    label: 'My Resources',    icon: FolderOpen },
  { id: 'curriculum-hub',  label: 'Curriculum Hub',  icon: Library },
  { id: 'settings',        label: 'Settings',        icon: Settings },
];

const TOOL_CARDS: {
  navItem: NavItem; label: string; description: string;
  icon: React.ElementType; gradient: string; light: string; ring: string; iconColor: string;
}[] = [
  {
    navItem: 'lesson-planner', label: 'Create a Lesson',
    description: 'Build a complete lesson plan with objectives, activities, and materials.',
    icon: BookOpen, gradient: 'from-blue-500 to-blue-600', light: 'bg-blue-50', ring: 'ring-blue-100', iconColor: 'text-blue-600',
  },
  {
    navItem: 'activity-sheets', label: 'Create an Activity Sheet',
    description: 'Design engaging worksheets and exercises for your students.',
    icon: FileText, gradient: 'from-violet-500 to-violet-600', light: 'bg-violet-50', ring: 'ring-violet-100', iconColor: 'text-violet-600',
  },
  {
    navItem: 'presentations', label: 'Create a Presentation',
    description: 'Produce polished slide decks ready for the classroom.',
    icon: Monitor, gradient: 'from-amber-500 to-orange-500', light: 'bg-amber-50', ring: 'ring-amber-100', iconColor: 'text-amber-600',
  },
  {
    navItem: 'assessments', label: 'Create an Assessment',
    description: 'Generate quizzes, tests, and rubrics tailored to your curriculum.',
    icon: ClipboardList, gradient: 'from-emerald-500 to-emerald-600', light: 'bg-emerald-50', ring: 'ring-emerald-100', iconColor: 'text-emerald-600',
  },
];

function DashboardPage() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { resources, deleteResource, duplicateResource, authExpiredWork, clearAuthExpiredWork, saveStatuses } = useResources();
  const { profile, loading: profileLoading, isProfileComplete, onboardingDone, updateProfile } = useProfile();
  const { billing, hasAccess, refresh: refreshBilling } = useBilling();
  const [, setLocation] = useLocation();
  const [activeNav, setActiveNav] = useState<NavItem>('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openResource, setOpenResource] = useState<SavedResource | null>(null);
  const [lessonContext, setLessonContext] = useState<{
    fromLessonTitle?: string;
    fromLessonId?: string;
    fromWorkspace?: boolean;
  } | null>(null);
  /** ID of the lesson currently open in the Lesson Workspace. */
  const [workspaceLessonId, setWorkspaceLessonId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // Curriculum Hub → Lesson Planner navigation state
  const [initialCurriculumMode, setInitialCurriculumMode] = useState(false);
  const [initialCurriculumId,   setInitialCurriculumId]   = useState<number | null>(null);
  // Welcome tour: shown once after profile setup when no resources exist yet
  const [tourDismissed, setTourDismissed] = useState(() =>
    localStorage.getItem('tb-welcome-tour-dismissed') === '1'
  );

  function dismissTour() {
    setTourDismissed(true);
    localStorage.setItem('tb-welcome-tour-dismissed', '1');
  }

  // Clear Hub-navigation state when teacher leaves the lesson planner
  React.useEffect(() => {
    if (activeNav !== 'lesson-planner') {
      setInitialCurriculumMode(false);
      setInitialCurriculumId(null);
    }
  }, [activeNav]);
  const [dashNotif, setDashNotif] = useState<string | null>(null);

  // Onboarding: show wizard when profile incomplete & not yet finished (DB-persisted — no localStorage)
  const showOnboarding = !profileLoading && profile !== null && !isProfileComplete && !onboardingDone;

  // Resources sorted by most-recently-viewed for "Continue where you left off"
  const recentlyViewed = React.useMemo(() =>
    [...resources]
      .filter(r => !!r.lastViewedAt)
      .sort((a, b) => (b.lastViewedAt ?? '').localeCompare(a.lastViewedAt ?? ''))
      .slice(0, 3),
    [resources]
  );

  // Derived from Clerk user
  const displayName = user?.fullName ?? user?.firstName ?? 'Teacher';
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';
  const userId    = user?.id ?? '';
  const userName  = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const userEmail = user?.primaryEmailAddress?.emailAddress ?? '';

  // Admin check — one lightweight call per userId change; never blocks the dashboard
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!userId) return;
    const base = import.meta.env.BASE_URL?.replace(/\/$/, '') ?? '';
    fetch(`${base}/api/admin/check`)
      .then(r => r.ok ? r.json() : { isAdmin: false })
      .then((d: any) => setIsAdmin(!!d.isAdmin))
      .catch(() => {});
  }, [userId]);

  function showDashNotif(msg: string) {
    setDashNotif(msg);
    setTimeout(() => setDashNotif(null), 3000);
  }

  /**
   * Checkout return handler — fires once on mount.
   *
   * When Stripe redirects the checkout tab back to /dashboard?billing=success
   * we: (a) refresh billing so the teacher's new plan shows immediately,
   * (b) broadcast 'billing-updated' to the original tab via BroadcastChannel,
   * (c) notify the teacher with a success banner, and
   * (d) try to close this checkout tab so the teacher lands back on their
   *     original tab automatically.
   */
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') !== 'success') return;
    // Clean URL before any async work
    window.history.replaceState({}, '', window.location.pathname);
    // Refresh billing on this tab
    refreshBilling();
    // Notify the original tab immediately
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel('teachbud-billing');
      ch.postMessage({ type: 'billing-updated' });
      ch.close();
    }
    // Show success banner
    setDashNotif('🎉 Subscription activated — welcome to TeachBud Pro!');
    // Try to close this checkout tab so the teacher returns to their original tab.
    // window.close() only works when the tab was opened by script (window.open);
    // if the browser blocks it, the teacher sees the dashboard with the success banner.
    const t = setTimeout(() => { try { window.close(); } catch { /* ignore */ } }, 2200);
    return () => clearTimeout(t);
    // refreshBilling is stable (useCallback with [user] dep) — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSignOut() {
    signOut({ redirectUrl: basePath || '/' });
  }

  function NavButton({ item }: { item: typeof NAV_ITEMS[0] }) {
    const Icon = item.icon;
    const active = activeNav === item.id;
    return (
      <button
        onClick={() => { setActiveNav(item.id); setSidebarOpen(false); }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group ${
          active
            ? 'bg-primary text-white shadow-sm shadow-primary/20'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`}
      >
        <Icon size={18} className={`flex-shrink-0 ${active ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'} transition-colors`} />
        <span>{item.label}</span>
        {active && <ChevronRight size={14} className="ml-auto text-white/70" />}
      </button>
    );
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-slate-100">
        <BrandMark onHomeClick={() => { setActiveNav('home'); setSidebarOpen(false); }} />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.filter(i => i.id !== 'settings').map((item) => (
          <NavButton key={item.id} item={item} />
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-slate-100 space-y-1">
        <NavButton item={NAV_ITEMS.find(i => i.id === 'settings')!} />
        {isAdmin && (
          <button
            onClick={() => { setLocation('/admin/messages'); setSidebarOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left
              text-slate-500 hover:text-primary hover:bg-primary/5 transition-all text-sm"
          >
            <Shield size={16} className="flex-shrink-0" />
            <span className="font-medium">Admin Messages</span>
          </button>
        )}
        <div className="flex items-center gap-3 px-3 py-2.5 mt-2">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{displayName}</p>
            <p className="text-xs text-slate-400 truncate">{email}</p>
          </div>
          <button onClick={handleSignOut} title="Sign out"
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-[100dvh] w-full bg-slate-50 overflow-hidden print:block print:h-auto print:overflow-visible">

      {/* Onboarding wizard — shown once after registration */}
      <AnimatePresence>
        {showOnboarding && (
          <OnboardingWizard
            userId={userId}
            onComplete={() => {
              // updateProfile with onboardingCompletedAt is already called
              // inside the wizard's handleFinish — nothing extra needed here
            }}
            onSkip={() => {
              updateProfile({ onboardingSkippedAt: new Date().toISOString() });
            }}
          />
        )}
      </AnimatePresence>

      {/* Global delete confirmation modal — used by ResourceRow on the home tab */}
      <AnimatePresence>
        {deleteConfirmId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setDeleteConfirmId(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full z-10">
              <p className="text-slate-800 font-semibold text-center mb-1">Delete Resource?</p>
              <p className="text-slate-500 text-sm text-center mb-6">
                This resource will be permanently removed from My Resources.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirmId(null)}
                  className="flex-1 h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button onClick={() => { deleteResource(deleteConfirmId); setDeleteConfirmId(null); showDashNotif('Resource deleted.'); }}
                  className="flex-1 h-10 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors">
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Notification toast */}
      <AnimatePresence>
        {dashNotif && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-xl bg-slate-900 text-white text-sm font-semibold pointer-events-none print:hidden"
          >
            <CheckCircle2 size={16} />
            {dashNotif}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 flex-shrink-0 bg-white border-r border-slate-100 shadow-sm print:hidden">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/30 z-40 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -240 }} animate={{ x: 0 }} exit={{ x: -240 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-y-0 left-0 w-60 bg-white border-r border-slate-100 shadow-xl z-50 flex flex-col md:hidden"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden print:block print:overflow-visible">

        {/* Top header */}
        <header className="flex-shrink-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between shadow-sm print:hidden">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
              onClick={() => setSidebarOpen(true)}>
              <Menu size={20} />
            </button>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {activeNav === 'home'
                  ? getGreeting(displayName)
                  : (NAV_ITEMS.find(i => i.id === activeNav)?.label ?? 'TeachBud')}
              </h2>
              <p className="text-sm text-slate-500 hidden sm:block">
                {activeNav === 'home' ? 'What would you like to create today?' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-slate-800">{displayName}</p>
              <p className="text-xs text-slate-400">{email}</p>
            </div>
            <div className="relative group">
              <button className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold hover:ring-4 hover:ring-primary/20 transition-all">
                {initials}
              </button>
              <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-xl shadow-lg border border-slate-100 py-1.5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                <button onClick={() => setActiveNav('settings')}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
                  <Settings size={15} />
                  Settings
                </button>
                <button onClick={handleSignOut}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors">
                  <LogOut size={15} />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Trial / billing status banner */}
        <div className="flex-shrink-0 print:hidden">
          <TrialBanner />
        </div>

        {/* Profile completion nudge */}
        {!isProfileComplete && activeNav === 'home' && (
          <div className="flex-shrink-0 bg-amber-50 border-b border-amber-100 px-6 py-3 flex items-center gap-3 print:hidden">
            <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 flex-1">
              <span className="font-semibold">Complete your profile</span> — add your state, grades, and subjects to personalize your lessons.
            </p>
            <button onClick={() => setActiveNav('settings')}
              className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline whitespace-nowrap">
              Go to Settings →
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto px-6 py-8 print:overflow-visible print:h-auto">

          {/* Welcome-back banner (shown once after reactivation) */}
          <WelcomeBackBanner />

          {/* Expired state — teacher can still view resources but cannot generate */}
          {!hasAccess && billing.status === 'expired' ? (
            <LockedState onViewResources={() => setActiveNav('my-resources')} />
          ) : activeNav === 'home' ? (
            <motion.div key="home" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl mx-auto space-y-10">

              {/* Hero banner — personalised when profile is complete */}
              <div className="rounded-2xl bg-gradient-to-br from-primary to-blue-700 p-6 md:p-8 text-white flex flex-col md:flex-row items-start md:items-center gap-4 shadow-lg shadow-primary/20">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  {isProfileComplete && profile ? (
                    <>
                      <h3 className="text-lg font-bold mb-1">
                        {profile.grades.length === 1 && profile.subjects.length === 1
                          ? `Grade ${profile.grades[0]} ${profile.subjects[0]} — ready to go`
                          : profile.grades.length > 0
                          ? `Grade${profile.grades.length > 1 ? 's' : ''} ${profile.grades.join(', ')} — ready to go`
                          : 'Your classroom is set up'}
                      </h3>
                      <p className="text-blue-100 text-sm leading-relaxed">
                        {profile.state ? `${profile.state} standards • ` : ''}
                        {profile.defaultDuration ? `${profile.defaultDuration}-minute lessons • ` : ''}
                        Everything pre-filled. Just pick a tool below.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-bold mb-1">Get started in seconds</h3>
                      <p className="text-blue-100 text-sm leading-relaxed">
                        TeachBud uses AI to help you create lessons, worksheets, assessments, and presentations — all tailored to your class and curriculum.
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* First-run welcome tour: show to any teacher who has completed/skipped
                  onboarding and hasn't generated anything yet */}
              <AnimatePresence>
                {onboardingDone && resources.length === 0 && !tourDismissed && (
                  <WelcomeTour
                    onDismiss={dismissTour}
                    onTryDemo={() => { dismissTour(); setActiveNav('lesson-planner'); }}
                  />
                )}
              </AnimatePresence>

              {/* Auth-expiry recovery notice */}
              {authExpiredWork && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3 print:hidden">
                  <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Session expired during a save</p>
                    <p className="text-xs text-amber-600 mt-1 leading-relaxed">
                      Your session expired while TeachBud was saving. We automatically retried after sign-in.
                      Resources showing "Save failed" have a <strong>Retry</strong> button — open the resource to use it.
                    </p>
                  </div>
                  <button onClick={clearAuthExpiredWork}
                    className="text-amber-400 hover:text-amber-600 transition-colors ml-2 flex-shrink-0">
                    <X size={15} />
                  </button>
                </div>
              )}

              {/* Failed saves notice */}
              {Object.values(saveStatuses).some(s => s === 'failed') && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-3 flex items-center gap-2.5 print:hidden">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-700">
                    <span className="font-semibold">Some resources failed to save.</span>{' '}
                    Open the affected resource and click <strong>Retry</strong> to try again.
                  </p>
                </div>
              )}

              {/* Continue where you left off */}
              {recentlyViewed.length > 0 && (
                <section>
                  <h3 className="text-base font-bold text-slate-700 mb-4 uppercase tracking-wide text-xs">Continue where you left off</h3>
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-50 overflow-hidden">
                    {recentlyViewed.map((r) => (
                      <ResourceRow key={r.id} resource={r} onDelete={(id) => setDeleteConfirmId(id)} />
                    ))}
                  </div>
                </section>
              )}

              {/* Tool cards */}
              <section>
                <h3 className="text-base font-bold text-slate-700 mb-4 uppercase tracking-wide text-xs">Create something new</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  {TOOL_CARDS.map((card) => {
                    const Icon = card.icon;
                    return (
                      <motion.button key={card.label} onClick={() => setActiveNav(card.navItem)}
                        whileHover={{ y: -3, scale: 1.01 }} whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className={`group text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 p-6 flex flex-col gap-4 transition-all duration-200 ring-0 hover:ring-4 ${card.ring}`}
                      >
                        <div className={`w-11 h-11 rounded-xl ${card.light} flex items-center justify-center`}>
                          <Icon size={22} className={card.iconColor} />
                        </div>
                        <div>
                          <p className="font-bold text-slate-900 mb-1.5 text-[15px] leading-snug">{card.label}</p>
                          <p className="text-slate-500 text-xs leading-relaxed">{card.description}</p>
                        </div>
                        <div className={`mt-auto self-start flex items-center gap-1 text-xs font-semibold bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent`}>
                          Get started <ChevronRight size={13} className={card.iconColor} />
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </section>

              {/* Recent resources */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-bold text-slate-700 uppercase tracking-wide text-xs">Recent Resources</h3>
                  {resources.length > 0 && (
                    <button onClick={() => setActiveNav('my-resources')}
                      className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                      View all →
                    </button>
                  )}
                </div>
                {resources.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 flex flex-col items-center justify-center text-center gap-5">
                    <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center">
                      <BookOpen size={28} className="text-primary/40" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-700 text-lg mb-1">Create your first resource</p>
                      <p className="text-slate-400 text-sm max-w-xs mx-auto leading-relaxed">
                        Generate a complete lesson plan, activity sheet, assessment, or presentation in under two minutes.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        onClick={() => setActiveNav('lesson-planner')}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all shadow-sm shadow-primary/20"
                      >
                        <BookOpen size={15} /> Generate Lesson
                      </button>
                      <button
                        onClick={() => setActiveNav('curriculum-hub')}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200 transition-all"
                      >
                        <Library size={15} /> Browse Curriculum Hub
                      </button>
                      <button
                        onClick={() => setActiveNav('my-resources')}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200 transition-all"
                      >
                        <FolderOpen size={15} /> My Resources
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-50 overflow-hidden">
                    {resources.slice(0, 5).map((r) => (
                      <ResourceRow key={r.id} resource={r} onDelete={(id) => setDeleteConfirmId(id)} />
                    ))}
                  </div>
                )}
              </section>
            </motion.div>

          ) : activeNav === 'lesson-planner' ? (
            <motion.div key="lesson-planner" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
              <LessonPlanner
                onGoHome={() => { setOpenResource(null); setActiveNav('home'); }}
                onGoMyResources={() => { setOpenResource(null); setActiveNav('my-resources'); }}
                onNavigateTo={(section, ctx) => {
                  setOpenResource(null);
                  setLessonContext(ctx ?? null);
                  setActiveNav(section as NavItem);
                }}
                onOpenWorkspace={(lessonId) => {
                  setWorkspaceLessonId(lessonId);
                  setOpenResource(null);
                  setLessonContext(null);
                  setActiveNav('lesson-workspace');
                }}
                initialResource={openResource}
                initialMode={initialCurriculumMode ? 'curriculum' : 'ai'}
                initialCurriculumId={initialCurriculumId ?? undefined}
                userId={userId}
              />
            </motion.div>

          ) : activeNav === 'activity-sheets' ? (
            <motion.div key="activity-sheets" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
              <ActivitySheet
                onBack={() => {
                  if (lessonContext?.fromWorkspace && workspaceLessonId) { setActiveNav('lesson-workspace'); }
                  else if (lessonContext?.fromLessonId) { setActiveNav('lesson-planner'); }
                  else { setLessonContext(null); setOpenResource(null); setActiveNav('home'); }
                }}
                onGoMyResources={() => { setLessonContext(null); setOpenResource(null); setActiveNav('my-resources'); }}
                onNavigateTo={(section, ctx) => {
                  setOpenResource(null);
                  setLessonContext(ctx ?? null);
                  setActiveNav(section as NavItem);
                }}
                lessonResource={lessonContext?.fromLessonId
                  ? resources.find(r => r.id === lessonContext.fromLessonId) ?? null
                  : null}
                initialResource={openResource?.type === 'activity' ? openResource : null}
              />
            </motion.div>

          ) : activeNav === 'curriculum-hub' ? (
            <motion.div key="curriculum-hub" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-4xl mx-auto">
              <CurriculumHub
                userId={userId}
                onUseInLesson={(id) => {
                  setInitialCurriculumId(id);
                  setInitialCurriculumMode(true);
                  setOpenResource(null);
                  setActiveNav('lesson-planner');
                }}
                onUploadNew={() => {
                  setInitialCurriculumId(null);
                  setInitialCurriculumMode(true);
                  setOpenResource(null);
                  setActiveNav('lesson-planner');
                }}
              />
            </motion.div>

          ) : activeNav === 'my-resources' ? (
            <motion.div key="my-resources" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-4xl mx-auto">
              <MyResourcesPage
                onBack={() => setActiveNav('home')}
                onOpenResource={(r) => {
                  // Lessons always open to the Lesson Workspace
                  if (r.type === 'lesson') {
                    setWorkspaceLessonId(r.id);
                    setOpenResource(null);
                    setLessonContext(null);
                    setActiveNav('lesson-workspace');
                    return;
                  }
                  setOpenResource(r);
                  // Companions with a parent lesson back-nav to the workspace
                  if (r.lessonId) {
                    setWorkspaceLessonId(r.lessonId);
                    setLessonContext({ fromLessonId: r.lessonId, fromWorkspace: true });
                  } else {
                    setLessonContext(null);
                  }
                  if (r.type === 'activity')          setActiveNav('activity-sheets');
                  else if (r.type === 'assessment')   setActiveNav('assessments');
                  else if (r.type === 'presentation') setActiveNav('presentations');
                }}
              />
            </motion.div>

          ) : activeNav === 'assessments' ? (
            <motion.div key="assessments" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl mx-auto">
              <AssessmentGenerator
                onBack={() => {
                  if (lessonContext?.fromWorkspace && workspaceLessonId) { setActiveNav('lesson-workspace'); }
                  else if (lessonContext?.fromLessonId) { setActiveNav('lesson-planner'); }
                  else { setLessonContext(null); setOpenResource(null); setActiveNav('home'); }
                }}
                onGoMyResources={() => { setLessonContext(null); setOpenResource(null); setActiveNav('my-resources'); }}
                onNavigateTo={(section, ctx) => {
                  setOpenResource(null);
                  setLessonContext(ctx ?? null);
                  setActiveNav(section as NavItem);
                }}
                lessonResource={lessonContext?.fromLessonId
                  ? resources.find(r => r.id === lessonContext.fromLessonId) ?? null
                  : null}
                initialResource={openResource?.type === 'assessment' ? openResource : null}
              />
            </motion.div>

          ) : activeNav === 'presentations' ? (
            <motion.div key="presentations" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl mx-auto">
              <PresentationGenerator
                onBack={() => {
                  if (lessonContext?.fromWorkspace && workspaceLessonId) { setActiveNav('lesson-workspace'); }
                  else if (lessonContext?.fromLessonId) { setActiveNav('lesson-planner'); }
                  else { setLessonContext(null); setOpenResource(null); setActiveNav('home'); }
                }}
                onGoMyResources={() => { setLessonContext(null); setOpenResource(null); setActiveNav('my-resources'); }}
                onNavigateTo={(section, ctx) => {
                  setOpenResource(null);
                  setLessonContext(ctx ?? null);
                  setActiveNav(section as NavItem);
                }}
                lessonResource={lessonContext?.fromLessonId
                  ? resources.find(r => r.id === lessonContext.fromLessonId) ?? null
                  : null}
                initialResource={openResource?.type === 'presentation' ? openResource : null}
              />
            </motion.div>

          ) : activeNav === 'lesson-workspace' ? (
            <motion.div key="lesson-workspace" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl mx-auto">
              {(() => {
                const workspaceLesson = workspaceLessonId
                  ? resources.find(r => r.id === workspaceLessonId) ?? null
                  : null;
                return workspaceLesson ? (
                  <LessonWorkspace
                    lessonResource={workspaceLesson}
                    onBack={() => { setActiveNav('my-resources'); }}
                    onNavigateTo={(section, ctx) => {
                      setOpenResource(null);
                      setLessonContext(ctx ?? null);
                      setActiveNav(section as NavItem);
                    }}
                    onOpenResource={(r) => {
                      setOpenResource(r);
                      setLessonContext({
                        fromLessonId: workspaceLessonId ?? undefined,
                        fromWorkspace: true,
                      });
                      if (r.type === 'activity')          setActiveNav('activity-sheets');
                      else if (r.type === 'assessment')   setActiveNav('assessments');
                      else if (r.type === 'presentation') setActiveNav('presentations');
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3 text-slate-400">
                    <BookOpen size={32} />
                    <p className="text-sm">Lesson not found.</p>
                    <button onClick={() => setActiveNav('my-resources')} className="text-sm text-primary hover:underline">
                      Go to My Resources
                    </button>
                  </div>
                );
              })()}
            </motion.div>

          ) : activeNav === 'settings' ? (
            <motion.div key="settings" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
              <SettingsPage onBack={() => setActiveNav('home')} />
            </motion.div>

          ) : null}
        </main>
      </div>

      {/* Help & Feedback — always visible, fixed bottom-right */}
      <HelpFeedbackButton
        userName={userName}
        userEmail={userEmail}
        currentPage={activeNav}
        resourceType={openResource?.type ?? null}
        resourceId={openResource?.id ?? null}
      />
    </div>
  );
}

// ── Resource card (My Resources grid) ────────────────────────────────────────
const RESOURCE_TYPE_META: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  lesson:       { label: 'Lesson',       color: 'bg-blue-100 text-blue-700',        icon: BookOpen },
  activity:     { label: 'Activity',     color: 'bg-violet-100 text-violet-700',    icon: FileText },
  assessment:   { label: 'Assessment',   color: 'bg-emerald-100 text-emerald-700',  icon: ClipboardList },
  presentation: { label: 'Presentation', color: 'bg-amber-100 text-amber-700',      icon: Monitor },
};

// ── Resource row (Recent Resources list) ──────────────────────────────────────
function ResourceRow({ resource, onDelete }: { resource: SavedResource; onDelete: (id: string) => void }) {
  const meta = RESOURCE_TYPE_META[resource.type] ?? RESOURCE_TYPE_META.lesson;
  const MetaIcon = meta.icon;
  const gradeLabel = resource.grade === 'K' ? 'Kindergarten' : `Grade ${resource.grade}`;
  const dateStr = new Date(resource.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  function handleCopy() {
    navigator.clipboard.writeText(resource.plainText).catch(() => {});
  }

  return (
    <div className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50/80 transition-colors group">
      <div className={`w-9 h-9 rounded-xl ${meta.color.split(' ')[0]} bg-opacity-60 flex items-center justify-center flex-shrink-0`}>
        <MetaIcon size={16} className={meta.color.split(' ')[1]} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{resource.title}</p>
        <p className="text-xs text-slate-400 mt-0.5">{gradeLabel} · {resource.subject} · {dateStr}</p>
      </div>
      <span className={`hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
        {meta.label}
      </span>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={handleCopy} title="Copy to clipboard"
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all">
          <BookMarked size={14} />
        </button>
        <button onClick={() => onDelete(resource.id)} title="Delete resource"
          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Protected dashboard (auth-guarded) ────────────────────────────────────────
function ProtectedDashboard() {
  return (
    <>
      <Show when="signed-in">
        <ResourcesProvider>
          <ProfileProvider>
            <BillingProvider>
              <DashboardPage />
            </BillingProvider>
          </ProfileProvider>
        </ResourcesProvider>
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

function ProtectedUpgradePage() {
  return (
    <>
      <Show when="signed-in">
        <ProfileProvider>
          <BillingProvider>
            <UpgradePage />
          </BillingProvider>
        </ProfileProvider>
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

function ProtectedAccountPage() {
  return (
    <>
      <Show when="signed-in">
        <ProfileProvider>
          <BillingProvider>
            <AccountStatusPage onBack={() => window.history.back()} />
          </BillingProvider>
        </ProfileProvider>
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

function ProtectedAdminPage() {
  const [, setLocation] = useLocation();
  return (
    <>
      <Show when="signed-in">
        <AdminMessagesPage onBack={() => setLocation('/dashboard')} />
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

// ── Home: landing for guests, portal redirect for signed-in ──────────────────
function HomeRedirect() {
  return (
    <>
      <Show when="signed-in">
        <Redirect to="/dashboard" />
      </Show>
      <Show when="signed-out">
        <HomePage />
      </Show>
    </>
  );
}

// ── ClerkProvider + router ────────────────────────────────────────────────────
function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: {
          start: { title: 'Welcome back', subtitle: 'Sign in to your TeachBud account' },
        },
        signUp: {
          start: { title: 'Create your account', subtitle: 'Start creating better lessons today' },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <TooltipProvider>
          <ErrorBoundary>
            <Switch>
              <Route path="/" component={HomeRedirect} />
              {/* REQUIRED — copy "/sign-in/*?" and "/sign-up/*?" verbatim */}
              <Route path="/sign-in/*?" component={SignInPage} />
              <Route path="/sign-up/*?" component={SignUpPage} />
              <Route path="/dashboard" component={ProtectedDashboard} />
              <Route path="/upgrade" component={ProtectedUpgradePage} />
              <Route path="/account" component={ProtectedAccountPage} />
              <Route path="/admin/messages" component={ProtectedAdminPage} />
              {/* Legal pages — publicly accessible */}
              <Route path="/privacy" component={() => <PrivacyPolicyPage onBack={() => window.history.back()} />} />
              <Route path="/terms" component={() => <TermsPage onBack={() => window.history.back()} />} />
              <Route component={NotFound} />
            </Switch>
          </ErrorBoundary>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
